/**
 * O chat do escritório — a tecla T.
 *
 * *"um sistema de histórico de mensagem, semi transparente, estilo os de jogos,
 * quando apertar T vai aparecer no meio da tela esse chat, sendo possível ver
 * msgs recentes e escrever"* — e depois, *"diminuir um pouco o tamanho e
 * colocar no canto esquerdo superior"*.
 *
 * Cada frase continua a ser a de sempre (`gatewayClient().say`): sai num balão
 * por cima da cabeça de quem a disse, para quem está perto, e agora fica também
 * nesta lista, que é a do escritório inteiro. O histórico curto vem do servidor
 * ao entrar (`SpaceState.chat`) e cresce a cada `SpaceSay`.
 *
 * Enviar não fecha o chat — é uma conversa. Fecha com Esc, com o × ou a
 * carregar no mapa. Enquanto está aberto, o teclado do jogo está desligado por
 * quem nos monta (senão o "w" de "wow" punha o boneco a andar).
 */
import { SAY_MAX_LENGTH, SAY_MIN_INTERVAL_MS, type Snowflake, type SpaceChatLine } from "@greenvoice/shared";
import { useEffect, useRef, useState } from "react";

import { gatewayClient, useStore } from "../store/store";
import { interpretarComando } from "./comandosDoChat";
import { IconClose } from "./Icons";
import { escolherTempo } from "./officeClima";

const SEM_LINHAS: SpaceChatLine[] = [];

/** Uma linha que só quem a escreveu vê: o comando e a resposta dele. */
interface LinhaLocal {
  at: number;
  text: string;
  /** `true` na resposta do sistema, `false` no comando que se escreveu. */
  sistema: boolean;
}

/**
 * As linhas locais de cada escritório. Ficam fora do componente para não se
 * perderem ao fechar e reabrir o chat; nunca vão ao servidor.
 */
const locaisPorEspaco = new Map<Snowflake, LinhaLocal[]>();

/** Cores dos nomes, legíveis sobre o fundo escuro. */
const CORES = ["#7dd3fc", "#fca5a5", "#fcd34d", "#c4b5fd", "#f9a8d4", "#fdba74", "#5eead4", "#a5b4fc"] as const;
const COR_PROPRIA = "#4ade80";

/** A cor fixa de uma pessoa: a mesma em todos os ecrãs e em todas as sessões. */
export function corDoNome(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return CORES[h % CORES.length] ?? CORES[0];
}

/** `15:07` — a hora da frase, no relógio de quem lê. */
export function horaDaLinha(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function OfficeChat({ spaceId, onClose }: { spaceId: Snowflake; onClose: () => void }) {
  const linhas = useStore((s) => s.spaceChat[spaceId] ?? SEM_LINHAS);
  const users = useStore((s) => s.users);
  const selfId = useStore((s) => s.self?.id ?? null);
  const [texto, setTexto] = useState("");
  const [locais, setLocais] = useState<LinhaLocal[]>(() => locaisPorEspaco.get(spaceId) ?? []);
  const painelRef = useRef<HTMLDivElement>(null);
  const listaRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Quando pode sair a próxima frase sem o servidor a deitar fora (anti-flood). */
  const proximoEnvio = useRef(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // A frase mais recente fica sempre à vista.
  const total = linhas.length + locais.length;
  useEffect(() => {
    const lista = listaRef.current;
    if (lista && total >= 0) lista.scrollTop = lista.scrollHeight;
  }, [total]);

  // Carregar no mapa fecha — como sair de uma caixa de chat num jogo.
  useEffect(() => {
    const fora = (e: PointerEvent) => {
      if (painelRef.current && !painelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("pointerdown", fora, true);
    return () => document.removeEventListener("pointerdown", fora, true);
  }, [onClose]);

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    const limpo = texto.trim();
    if (!limpo) return;
    // Um comando (`/…`) não sai daqui: nem balão, nem histórico, nem rede.
    // Quem o escreveu vê-o, com a resposta, e mais ninguém.
    const comando = interpretarComando(limpo);
    if (comando) {
      if (comando.acao?.tipo === "clima") escolherTempo(comando.acao.tempo);
      const agora = Date.now();
      const novas = [
        ...locais,
        { at: agora, text: limpo, sistema: false },
        { at: agora + 1, text: comando.resposta, sistema: true },
      ].slice(-20);
      locaisPorEspaco.set(spaceId, novas);
      setLocais(novas);
      setTexto("");
      return;
    }
    // O servidor ignora frases a menos de `SAY_MIN_INTERVAL_MS` umas das outras.
    // Quem escreve depressa não pode perder mensagens por isso: esperam a vez.
    const agora = Date.now();
    const quando = Math.max(agora, proximoEnvio.current);
    proximoEnvio.current = quando + SAY_MIN_INTERVAL_MS + 50;
    const mandar = () => gatewayClient()?.say(spaceId, limpo);
    if (quando === agora) mandar();
    else window.setTimeout(mandar, quando - agora);
    setTexto("");
  };

  const nomeDe = (userId: Snowflake): string => {
    const u = users[userId];
    return u ? (u.displayName ?? u.username) : "Alguém";
  };

  return (
    <div className="office-chat">
      <div ref={painelRef} className="office-chat__painel" role="dialog" aria-label="Chat do escritório">
        <header className="office-chat__cab">
          <span>Chat do escritório</span>
          <span className="office-chat__dica">
            <kbd>Enter</kbd> envia · <kbd>Esc</kbd> fecha
          </span>
          <button type="button" className="office-chat__fechar" onClick={onClose} aria-label="Fechar o chat">
            <IconClose size={14} />
          </button>
        </header>
        <ol ref={listaRef} className="office-chat__lista" aria-live="polite">
          {linhas.length === 0 && locais.length === 0 ? (
            <li className="office-chat__vazio">Ainda ninguém escreveu nada. Diz olá. (/ajuda para os comandos)</li>
          ) : (
            [
              ...linhas.map((l) => ({ tipo: "frase" as const, at: l.at, l })),
              ...locais.map((l) => ({ tipo: "local" as const, at: l.at, l })),
            ]
              .sort((a, b) => a.at - b.at)
              .map((item) =>
                item.tipo === "frase" ? (
                  // Única sem índice: o servidor não aceita duas frases da mesma
                  // pessoa no mesmo instante (`SAY_MIN_INTERVAL_MS`).
                  <li key={`${item.l.at}-${item.l.userId}-${item.l.text}`} className="office-chat__linha">
                    <time className="office-chat__hora" dateTime={new Date(item.l.at).toISOString()}>
                      {horaDaLinha(item.l.at)}
                    </time>
                    <span
                      className="office-chat__nome"
                      style={{ color: item.l.userId === selfId ? COR_PROPRIA : corDoNome(item.l.userId) }}
                    >
                      {nomeDe(item.l.userId)}
                    </span>
                    <span className="office-chat__texto">{item.l.text}</span>
                  </li>
                ) : (
                  <li
                    key={`local-${item.l.at}`}
                    className={`office-chat__linha office-chat__linha--local${item.l.sistema ? " is-sistema" : ""}`}
                    title="Só tu vês esta linha"
                  >
                    <time className="office-chat__hora" dateTime={new Date(item.l.at).toISOString()}>
                      {horaDaLinha(item.l.at)}
                    </time>
                    {item.l.sistema ? null : (
                      <span className="office-chat__nome" style={{ color: COR_PROPRIA }}>
                        {selfId ? nomeDe(selfId) : "Tu"}
                      </span>
                    )}
                    <span className="office-chat__texto">{item.l.text}</span>
                    <span className="office-chat__so-tu">só tu vês</span>
                  </li>
                ),
              )
          )}
        </ol>
        <form className="office-chat__form" onSubmit={enviar}>
          <input
            ref={inputRef}
            className="office-chat__input"
            value={texto}
            maxLength={SAY_MAX_LENGTH}
            placeholder="Escreve para o escritório…"
            aria-label="Mensagem"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              // O Phaser ouve o teclado na WINDOW; parar aqui é o que impede o
              // jogo de usar as letras que se escrevem para andar.
              e.stopPropagation();
              if (e.key === "Escape") {
                e.preventDefault();
                onClose();
              }
            }}
          />
          <button type="submit" className="office-chat__enviar" disabled={texto.trim() === ""}>
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
}
